import { attributeValue } from 'func-xml';
import { isNil } from 'ramda';
import currencyLocales from '../../../currencyLocales';
import { getXmlDate } from '../../utility/date.helpers';
import { debounce } from '../../utility/eventHelpers';
import { hasYesAttribute, xmlStringToBoolean } from '../../utility/xmlHelpers';
import constants from '../constants';
import {
    CTRL_ID,
    REPEATER,
    XCTNAME,
    XMLACTION,
} from '../constantsFormAttributes';
import fileDropWatcher from '../fileDropWatcher';
import { addPeopleToCustomPicker } from '../helpers/peoplePicker.helpers';
import { getSearchValues } from '../UI/peoplePickerComponent';
import { isDisabledValue } from '../uiHelpers/viewHelpers';
import { getHyperlinkBoxClickAction } from '../view/hyperlinkBox.helpers';
import { getMultiSelectChangeAction } from '../view/multiSelectHelpers';
import {
    ATTR_ACTION,
    ATTR_CONTEXT,
    ATTR_DISABLED,
    ATTR_DISABLE_EDITING,
    ATTR_EDITABLE_SELECTOR,
    ATTR_HAS_VALIDATION_ERROR,
    ATTR_HAS_VALUE,
    ATTR_OFF_VALUE,
    ATTR_ON_VALUE,
    ATTR_XMLTOEDIT,
    CLICKABLE_SELECTOR,
} from '../view/view.attribute.names';
import { CLASSNAME_HYPERLINK_BOX } from '../view/view.class.names';
import { getNodeId } from '../view/viewNodeAccessors';
import {
    VIEW_EVENT_BUTTON,
    VIEW_EVENT_CUSTOM_SEARCH_CHANGE,
    VIEW_EVENT_FIELDCHANGE,
    VIEW_EVENT_FILEDROP,
    VIEW_EVENT_INSERT,
    VIEW_EVENT_MENUCLICK,
    VIEW_EVENT_PEOPLE_ADDRESS,
    VIEW_EVENT_PEOPLE_CHANGE,
    VIEW_EVENT_PEOPLE_SEARCH,
    VIEW_EVENT_SHOWMENU,
    VIEW_EVENT_SHOWMENUHANDLE,
} from '../viewActionHandler/viewActionHandler.constants';
import viewAction from './viewAction';
import { handleTextKeyUp } from './viewWatcher.textBoxKeyHandler';
// import { addSelectionItemToCustomPicker } from '../helpers/peoplePicker.helpers';

const $ = window.jQuery;
const CKEDITOR = window.CKEDITOR;

const SELECTOR_EDITABLE_HYPERLINK_BOX = `.${CLASSNAME_HYPERLINK_BOX}:not([${ATTR_DISABLE_EDITING}=yes])`;

const SELECTOR_HYPERLINK_BOX_CLICKABLE = `${SELECTOR_EDITABLE_HYPERLINK_BOX} input[type=button], ${SELECTOR_EDITABLE_HYPERLINK_BOX}:not([${ATTR_HAS_VALUE}=true])`;

// ViewWatcher
// Responsible for receiving DOM events from the DOM,
//     collecting and assembling the relevant information from the DOM based on those events,
//     and then passing the processed information to registered listener(s)

function getNodeFormatAttr(target) {
    const fmtVal = target.getAttribute('data-fv-xd-datafmt');

    if (!fmtVal) {
        return null;
    }

    //07/12/2017-- Change split to use "(double quotes) as date format may contain ,(comma)

    const fmtValSplit = fmtVal.split('"');

    let fmtAttrList = {
        fmtType: fmtValSplit[1],
    };

    if (fmtValSplit[3]) {
        const fmtAttr = fmtValSplit[3].split(';');

        fmtAttr.forEach(function (attr) {
            const attrSplit = attr.split(':');

            if (attrSplit.length > 1) {
                fmtAttrList[attrSplit[0]] = attrSplit[1];
            }
        });
    }

    return fmtAttrList;
}

// Making this "static" because it needs to be accessed from two different types.
// Need to decide whether there's a better way to do this.
function getEventNodeProps(target) {
    const jTarg = $(target);
    const fmtAttrList = getNodeFormatAttr(target);
    const formatType = fmtAttrList ? fmtAttrList.fmtType : null;
    const currencyLocale = fmtAttrList ? fmtAttrList.currencyLocale : null;
    const hasGrouping =
        (formatType === 'currency' || formatType === 'number') &&
        fmtAttrList &&
        !fmtAttrList['grouping'];
    //Field has grouping if fmtAttrList does not have attribute "grouping", for field with no grouping "grouping" is 0

    return {
        target,
        newValue: target.value,
        inputType: jTarg.attr('type'),
        isRichText: jTarg.hasClass('fv-rich-text'),
        context: jTarg.attr(ATTR_CONTEXT),
        ctrlId: jTarg.attr(CTRL_ID),
        nodeId: getNodeId(target),
        action: jTarg.attr(ATTR_ACTION),
        disabled: isDisabledValue(jTarg.attr(ATTR_DISABLED)),
        formatType,
        currencyLocale,
        hasGrouping,
        controlType: jTarg.attr(XCTNAME),
        hasValidationError: hasYesAttribute(ATTR_HAS_VALIDATION_ERROR, target),
        disableEditing: hasYesAttribute(ATTR_DISABLE_EDITING, target),
    };
}

function getNewDateValue(target) {
    // 2023-10-25 - Removed unused retrieval of dateFormat and settings
    var datePicker = $(target),
        targetValue = datePicker.val(),
        selectedDate,
        isValid = true;

    try {
        //selectedDate = $.datepicker.parseDate(format, targetValue, settings);
        selectedDate = datePicker.datepicker('getDate');
    } catch (e) {
        isValid = false;
    }

    return {
        date: selectedDate ? getXmlDate(selectedDate) : targetValue,
        isValidDate: isValid,
    };
}

function viewWatcher(baseNode) {
    var listeners = [],
        $baseNode = $(baseNode);

    // 150 ms seems enough to overcome the rich text box blur delay
    var delayDuration = 150;

    function shouldDelay(event) {
        // delay events that can trigger a rule simply by clicking. this will give any rich text blur events time to fire before carring out these events
        if (event.type === VIEW_EVENT_BUTTON) {
            return true;
        }

        if (event.type === VIEW_EVENT_FIELDCHANGE && event.props) {
            var it = (event.props.inputType || '').toLowerCase();

            return it === 'checkbox' || it === 'radio';
        }

        return false;
    }

    function notifyListeners(event) {
        listeners.forEach(function (listener) {
            try {
                listener(event);
            } catch (e) {
                console.error('Error emitting event: ' + JSON.stringify(event));
                console.error(e);
            }
        });
    }

    function emit(event) {
        // Certain events are delayed by a small amount to allow rich text box blur events to happen first
        var delay = shouldDelay(event) ? delayDuration : 0;

        setTimeout(notifyListeners.bind(null, event), delay);
    }

    function hideWidgets() {
        $baseNode.find(constants.MenuWidgetClass).hide();
    }

    function formatFieldValueChanges(props) {
        switch (props.formatType) {
            case 'currency':
                var localeInfo = currencyLocales[props.currencyLocale];

                if (localeInfo) {
                    var valueWithoutComma = props.newValue
                            .replace(/,/g, '')
                            .trim(),
                        valueWithoutSymbol = valueWithoutComma.replace(
                            localeInfo.symbol,
                            '',
                        ),
                        isSymbolExists =
                            valueWithoutComma.indexOf(localeInfo.symbol) >= 0,
                        isCorrectPosition =
                            localeInfo.position === 'before'
                                ? valueWithoutComma.startsWith(
                                      localeInfo.symbol,
                                  )
                                : valueWithoutComma.endsWith(localeInfo.symbol);

                    if (
                        (!isSymbolExists || isCorrectPosition) &&
                        !Number.isNaN(Number(valueWithoutSymbol))
                    ) {
                        props.newValue = valueWithoutSymbol;
                    }
                }
                break;

            case 'percentage':
                var parsedValue = parseFloat(props.newValue.trim());

                if (!Number.isNaN(parsedValue)) {
                    props.newValue = parsedValue / 100;
                }
                break;

            case 'number':
                if (props.hasGrouping) {
                    props.newValue = props.newValue.replace(/,/g, '').trim();
                }
                break;
            case null:
                // unformatted
                break;
            default:
                console.warn(`Unknown format type: ${props.formatType}`);
                break;
        }
    }

    function getRichTextValue($element) {
        var id = $element.attr('id');

        if (!id) {
            console.warn('Unable to get ID for rich text box.');
            return '';
        }

        var rtb = CKEDITOR.instances[id];

        if (!rtb) {
            console.warn('Unable to locate rich text box for id ', id);
            return '';
        }

        return rtb.getData();
    }

    function fieldChange(e, value) {
        var elTarget = $(e.target);
        //TODO:Handle select2 change
        if (elTarget.hasClass('ppl-picker')) {
            return;
        }
        var props = getEventNodeProps(e.target);

        if (props.newValue) {
            formatFieldValueChanges(props);
        }

        if (!isNil(value)) {
            props.newValue = value;
        } else if (props.inputType === 'date') {
            props.newValue = getNewDateValue(props.target);
        } else if (props.isRichText) {
            props.newValue = getRichTextValue(elTarget);
        }

        const parentClassName = props.target.parentElement?.className;
        let controlType;

        switch (parentClassName) {
            case 'xdDatePicker':
            case 'xdDTPicker':
                controlType = 'datePicker';
                var dateObject = getNewDateValue(props.target);
                props.newValue = dateObject.date;
                props.isValidDate = dateObject.isValidDate;
                break;

            case 'xdTimeTextBoxWrap':
                controlType = 'timePicker';
                break;

            default:
                controlType = null;
                break;
        }

        emit(viewAction(VIEW_EVENT_FIELDCHANGE, props, { controlType }));
    }

    // Event handler for checkbox check events
    function checkChange(e) {
        const target = e.target;
        const value = target.getAttribute(
            target.checked ? ATTR_ON_VALUE : ATTR_OFF_VALUE,
        );

        fieldChange(e, value || '');
    }

    const multiSelectCheckChange = (e) => {
        const action = getMultiSelectChangeAction(e.target);

        emit(action);
    };

    const hyperlinkBoxClick = (evt) => {
        evt.stopPropagation();

        const box = evt.target.closest(`.${CLASSNAME_HYPERLINK_BOX}`);

        const action = getHyperlinkBoxClickAction(box);

        emit(action);
    };

    const isButtonTypeElement = (el) =>
        el.tagName === 'BUTTON' ||
        (el.tagName === 'INPUT' &&
            (el.type === 'button' || el.type === 'submit'));

    const shouldHandleClickable = (event, target) => {
        // from https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role
        if (!(event instanceof KeyboardEvent)) {
            return true;
        }

        if (isButtonTypeElement(target)) {
            return false;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            return true;
        }

        return false;
    };

    // jQueryClickEvent -> void
    // used as an event handler and therefore context is not the containing object
    function buttonClick(e) {
        const event = e.originalEvent || e;
        const target = e.currentTarget;

        if (!shouldHandleClickable(event, target)) {
            return;
        }

        const bProps = getEventNodeProps(target);

        if (!bProps.disabled) {
            emit(viewAction(VIEW_EVENT_BUTTON, bProps));
        }
    }

    function insertClick({ target }) {
        const xmlToEdit = attributeValue(ATTR_XMLTOEDIT)(target);

        emit(
            viewAction(VIEW_EVENT_INSERT, null, {
                id: getNodeId(target),
                xmlToEdit,
            }),
        );
    }

    function hideMenus() {
        $('.fv-menu-holder').hide();
    }

    function showMenu(e) {
        e.stopPropagation();

        emit(viewAction(VIEW_EVENT_SHOWMENU, null, { target: e.target }));
    }

    function showMenuHandle(e) {
        e.stopPropagation();

        hideMenus();

        emit({ type: VIEW_EVENT_SHOWMENUHANDLE, target: e.target });
    }

    function menuItemClick(e) {
        const target = $(e.target);
        const action = target.attr(XMLACTION);

        e.stopPropagation();

        hideMenus();

        emit(viewAction(VIEW_EVENT_MENUCLICK, null, { target, action }));
    }

    function getFileDropTargetType(xctName) {
        switch (xctName.toLowerCase()) {
            case 'inlineimage':
                return 'image';
            case 'fileattachment':
                return 'file';
            default:
                return '(unknown)';
        }
    }

    function fileDrop(evt) {
        const target = evt.target[0];

        const props = getEventNodeProps(target);
        const xctName = target.getAttribute('data-fv-xd-xctname') || '';

        emit(
            viewAction(VIEW_EVENT_FILEDROP, props, {
                data: evt.data,
                targetType: getFileDropTargetType(xctName),
            }),
        );
    }

    function getPeoplePickerProps(e, type) {
        var target = $(e.target);
        var selector = target.closest('.fv-PeoplePicker').find('.ppl-picker');
        var searchPeopleOnly = xmlStringToBoolean(
            selector.attr('searchPeopleOnly'),
        );
        var allowMultiple = xmlStringToBoolean(selector.attr('allowMultiple'));

        emit(
            viewAction(type, getEventNodeProps(selector[0]), {
                target,
                searchPeopleOnly,
                allowMultiple,
            }),
        );
    }

    function pplPickerSearchAddressBookClick(e) {
        getPeoplePickerProps(e, VIEW_EVENT_PEOPLE_ADDRESS);
    }

    function pplPickerCheckNamesClick(e) {
        getPeoplePickerProps(e, VIEW_EVENT_PEOPLE_SEARCH);
    }

    const getPeoplePickerData = ($target) => {
        return $target.data('selectedPpl') || [];
    };

    function peoplePickerChange(e) {
        const target = $(e.target);
        const selector = target.closest('.fv-PeoplePicker').find('.ppl-picker');
        const eventProps = getEventNodeProps(selector[0]);
        const data = getPeoplePickerData(target);

        emit(
            viewAction(VIEW_EVENT_PEOPLE_CHANGE, eventProps, {
                target,
                data,
            }),
        );
    }

    // Subscribe a listener for this viewWatcher.
    // Listener should be a function that accepts a single eventInfo parameter
    function subscribe(listener) {
        if (typeof listener !== 'function') {
            throw new TypeError('listener is not a function.');
        }

        listeners.push(listener);
    }

    function preventDefault(ev) {
        ev.preventDefault();
    }

    function hideDatePicker(evt) {
        $(evt.target).datepicker('hide');
    }

    // Handle click on custom dropdown items
    function customPeoplePickerDropdownItemClick(e) {
        const $item = $(e.currentTarget);
        const $dropdown = $item.closest('.custom-picker-dropdown');
        const $container = $dropdown.closest('.custom-people-picker');
        const $peoplePickerNode = $container.find('select').first();
        const accountId = $item.data('id');
        const EntityType = $item.data('EntityType');
        const displayName = $item.find('.picker-name').text();

        const item = {
            id: accountId,
            text: displayName,
            DisplayName: displayName,
            AccountId: accountId,
            EntityType: EntityType || 'User',
        };

        const isMulti = $peoplePickerNode.prop('multiple');

        const $input = $container.find('.custom-picker-input');

        $dropdown.hide();
        $input.val('').focus();

        addPeopleToCustomPicker($peoplePickerNode, [item], isMulti);
    }

    const debouncedEmitSearchEvent = debounce(function (props) {
        emit(viewAction(VIEW_EVENT_CUSTOM_SEARCH_CHANGE, props));
    }, 300);

    function handleCustomPickerInput(e) {
        const searchValues = getSearchValues(e.currentTarget);

        debouncedEmitSearchEvent(searchValues);
    }

    // Register DOM events on the target node (using event delegation)
    $baseNode
        .on(
            'change',
            ATTR_EDITABLE_SELECTOR + '[type != checkbox]',
            fieldChange,
        )
        .on('change', ATTR_EDITABLE_SELECTOR + '[type = checkbox]', checkChange)
        .on(
            'change',
            '.xdMultiSelectListItem input[type=checkbox]',
            multiSelectCheckChange,
        )
        .on('click', CLICKABLE_SELECTOR, buttonClick)
        .on('keyup', CLICKABLE_SELECTOR, buttonClick)
        .on('click', '[data-fv-xd-xmltoedit]', insertClick)
        .on('mouseenter click', REPEATER, showMenuHandle)
        .on('click', constants.MenuWidgetClass, showMenu)
        .on('focus', 'input', hideWidgets)
        .on(
            'click touchstart',
            'input[data-fv-xd-xctname=DTPicker_DTText]',
            hideDatePicker,
        )
        .on('click', constants.MenuWidgetClass + ' li', menuItemClick)
        .on('click', hideWidgets)
        .on('click', '.ppl-search', pplPickerSearchAddressBookClick)
        .on('click', '.ppl-check-names', pplPickerCheckNamesClick)
        .on(
            'keyup',
            '[data-fv-xd-xctname=PlainText]',
            handleTextKeyUp($baseNode),
        )
        .on('change', '.ppl-picker', peoplePickerChange)
        .on(
            'click',
            '.custom-picker-dropdown-item',
            customPeoplePickerDropdownItemClick,
        )
        .on('click', SELECTOR_HYPERLINK_BOX_CLICKABLE, hyperlinkBoxClick)
        .on('input', '.custom-picker-input', handleCustomPickerInput);

    // prevent navigating away from the page if file is dropped outside of file control
    $('body').on('dragover', preventDefault).on('drop', preventDefault);

    fileDropWatcher({
        container: $baseNode,
        onDrop: fileDrop,
    });

    // interface
    return {
        subscribe,
    };
}

export default viewWatcher;

export { getEventNodeProps, getNodeFormatAttr };
